import { deleteJSON, get, patchJSON, postJSON, putJSON } from '../../../utils/Request';

export type MenuItem = {
  menuNo?: string;
  parentCode?: string;
  displayName?: string;
  displayNameEn?: string;
  displayNameVi?: string;
  menuType?: string;
  routePath?: string;
  componentKey?: string;
  sortOrder?: number;
  status?: string;
  children?: MenuItem[];
};

export const MENU_TYPE_LABEL: Record<string, string> = {
  DIRECTORY: '目录',
  MENU: '页面菜单'
};

export const MENU_STATUS_LABEL: Record<string, string> = {
  ACTIVE: '启用',
  DISABLED: '停用'
};

export type MenuConfig = {
  data?: MenuItem;
  configHash?: string;
};

export type CreateMenuBody = {
  parentCode?: string;
  displayName: string;
  displayNameEn?: string;
  displayNameVi?: string;
  menuType: 'DIRECTORY' | 'MENU';
  routePath: string;
  componentKey?: string;
  sortOrder: number;
  status: 'ACTIVE' | 'DISABLED';
};

export type UpdateMenuBody = CreateMenuBody & {
  expectedConfigHash: string;
};

/** 查询菜单配置。content 为菜单数组，页面按 parentCode 组树。 */
export function getMenus() {
  return get<MenuItem[]>({ path: '/api/v1/menus' });
}

/** 新增菜单节点。不传 expectedConfigHash。目录不传 componentKey，根节点不传 parentCode。英语名、越南语名留空时不传。 */
export function createMenu(body: CreateMenuBody) {
  return postJSON<MenuConfig>({
    path: '/api/v1/menus',
    params: body
  });
}

/** 查询菜单详情。content.configHash 修改、删除、启用或停用时原样传回。 */
export function getMenuDetail(menuNo: string) {
  return get<MenuConfig>({
    path: `/api/v1/menus/${encodeURIComponent(menuNo)}`
  });
}

/** 修改菜单节点。expectedConfigHash 原样传回详情里的 configHash。英语名、越南语名留空时不传，全量保存会清空。 */
export function updateMenu(menuNo: string, body: UpdateMenuBody) {
  return putJSON<MenuConfig>({
    path: `/api/v1/menus/${encodeURIComponent(menuNo)}`,
    params: body
  });
}

/** 删除菜单节点。expectedConfigHash 原样传回详情里的 configHash。配置变化时返回 409。 */
export function deleteMenu(menuNo: string, expectedConfigHash: string) {
  return deleteJSON<null>({
    path: `/api/v1/menus/${encodeURIComponent(menuNo)}`,
    params: { expectedConfigHash }
  });
}

/** 启用或停用菜单。expectedConfigHash 原样传回详情里的 configHash。配置变化时返回 409。 */
export function updateMenuStatus(menuNo: string, status: 'ACTIVE' | 'DISABLED', expectedConfigHash: string) {
  return patchJSON<MenuConfig>({
    path: `/api/v1/menus/${encodeURIComponent(menuNo)}/status`,
    params: { status, expectedConfigHash }
  });
}

/** 根节点 parentCode 为空。同级按 sortOrder 升序。 */
export function buildMenuTree(list: MenuItem[]): MenuItem[] {
  const nodes = list.map((item) => ({ ...item, children: [] as MenuItem[] }));
  const byNo = new Map<string, MenuItem>();
  nodes.forEach((node) => {
    const key = node.menuNo?.trim();
    if (key && !byNo.has(key)) byNo.set(key, node);
  });
  const roots: MenuItem[] = [];
  nodes.forEach((node) => {
    const parentKey = node.parentCode?.trim();
    const selfKey = node.menuNo?.trim();
    const parent = parentKey && parentKey !== selfKey ? byNo.get(parentKey) : undefined;
    if (parent) parent.children!.push(node);
    else roots.push(node);
  });
  const sortLevel = (items: MenuItem[], stack: Set<MenuItem>): MenuItem[] =>
    items
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((item) => {
        if (stack.has(item)) return { ...item, children: undefined };
        stack.add(item);
        const children = item.children?.length ? sortLevel(item.children, stack) : undefined;
        stack.delete(item);
        return { ...item, children: children?.length ? children : undefined };
      });
  return sortLevel(roots, new Set());
}

export type MenuType = 'directory' | 'menu';

export type MenuI18nName = {
  zh: string;
  en: string;
  vi: string;
};

export type AdminMenu = {
  id: string;
  parentId: string | null;
  type: MenuType;
  name: MenuI18nName;
  region: string;
  icon: string;
  sort: number;
  visible: boolean;
  path: string;
  enabled: boolean;
  createdAt: string;
  children?: AdminMenu[];
};

export const MENU_ICON_OPTIONS = [
  { value: 'menuIcon-workbench.png', label: '工作台' },
  { value: 'menuIcon-tk.png', label: 'TikTok' },
  { value: 'menuIcon-shopee.png', label: 'Shopee' },
  { value: 'menuIcon-amazon.png', label: 'Amazon' },
  { value: 'menuIcon-1688.png', label: '1688' },
  { value: 'menuIcon-service.png', label: '配套服务' },
  { value: 'menuIcon-user.png', label: '用户中心' },
  { value: 'menuIcon-benefit.png', label: '权益购买' },
  { value: 'menuIcon-folder.png', label: '目录' },
  { value: 'menuIcon-default.png', label: '默认' }
];

export const adminMenuTree: AdminMenu[] = [
  {
    id: 'm-1',
    parentId: null,
    type: 'directory',
    name: { zh: '工作台', en: 'Workbench', vi: 'Bàn làm việc' },
    region: '越南',
    icon: 'menuIcon-workbench.png',
    sort: 1,
    visible: true,
    path: '/ranking/workbench',
    enabled: true,
    createdAt: '2020-10-10 10:10:10',
    children: [
      {
        id: 'm-1-1',
        parentId: 'm-1',
        type: 'menu',
        name: { zh: '数据概览', en: 'Overview', vi: 'Tổng quan' },
        region: '越南',
        icon: 'menuIcon-default.png',
        sort: 1,
        visible: true,
        path: '/ranking/workbench/overview',
        enabled: true,
        createdAt: '2020-10-10 10:12:10',
        children: [
          {
            id: 'm-1-1-1',
            parentId: 'm-1-1',
            type: 'menu',
            name: { zh: '今日指标', en: 'Today Metrics', vi: 'Chỉ số hôm nay' },
            region: '越南',
            icon: 'menuIcon-default.png',
            sort: 1,
            visible: true,
            path: '/ranking/workbench/overview/today',
            enabled: true,
            createdAt: '2020-10-11 09:00:00'
          }
        ]
      },
      {
        id: 'm-1-2',
        parentId: 'm-1',
        type: 'menu',
        name: { zh: '待办事项', en: 'Todos', vi: 'Việc cần làm' },
        region: '越南',
        icon: 'menuIcon-default.png',
        sort: 2,
        visible: true,
        path: '/ranking/workbench/todos',
        enabled: true,
        createdAt: '2020-10-10 10:13:10'
      }
    ]
  },
  {
    id: 'm-2',
    parentId: null,
    type: 'directory',
    name: { zh: 'TikTok', en: 'TikTok', vi: 'TikTok' },
    region: '越南',
    icon: 'menuIcon-tk.png',
    sort: 2,
    visible: true,
    path: '/ranking/tiktok/products',
    enabled: true,
    createdAt: '2020-10-10 10:10:10',
    children: [
      {
        id: 'm-2-1',
        parentId: 'm-2',
        type: 'menu',
        name: { zh: '商品榜', en: 'Products', vi: 'Sản phẩm' },
        region: '越南',
        icon: 'menuIcon-tk.png',
        sort: 1,
        visible: true,
        path: '/ranking/tiktok/products',
        enabled: true,
        createdAt: '2020-10-10 10:14:10'
      },
      {
        id: 'm-2-2',
        parentId: 'm-2',
        type: 'menu',
        name: { zh: '达人榜', en: 'Creators', vi: 'Nhà sáng tạo' },
        region: '越南',
        icon: 'menuIcon-tk.png',
        sort: 2,
        visible: true,
        path: '/ranking/tiktok/creators',
        enabled: false,
        createdAt: '2020-10-10 10:15:10'
      }
    ]
  },
  {
    id: 'm-3',
    parentId: null,
    type: 'directory',
    name: { zh: 'Shopee', en: 'Shopee', vi: 'Shopee' },
    region: '越南',
    icon: 'menuIcon-shopee.png',
    sort: 3,
    visible: true,
    path: '/ranking/shopee/products',
    enabled: true,
    createdAt: '2020-10-10 10:10:10',
    children: [
      {
        id: 'm-3-1',
        parentId: 'm-3',
        type: 'menu',
        name: { zh: '店铺列表', en: 'Shops', vi: 'Cửa hàng' },
        region: '越南',
        icon: 'menuIcon-shopee.png',
        sort: 1,
        visible: true,
        path: '/ranking/shopee/shops',
        enabled: true,
        createdAt: '2020-10-10 10:16:10'
      }
    ]
  },
  {
    id: 'm-4',
    parentId: null,
    type: 'menu',
    name: { zh: 'Amazon', en: 'Amazon', vi: 'Amazon' },
    region: '越南',
    icon: 'menuIcon-amazon.png',
    sort: 4,
    visible: true,
    path: '/ranking/amazon/products',
    enabled: true,
    createdAt: '2020-10-10 10:10:10'
  },
  {
    id: 'm-5',
    parentId: null,
    type: 'menu',
    name: { zh: '1688', en: '1688', vi: '1688' },
    region: '中国大陆',
    icon: 'menuIcon-1688.png',
    sort: 5,
    visible: true,
    path: '/ranking/1688/products',
    enabled: true,
    createdAt: '2020-10-10 10:10:10'
  },
  {
    id: 'm-6',
    parentId: null,
    type: 'menu',
    name: { zh: '配套服务', en: 'Services', vi: 'Dịch vụ hỗ trợ' },
    region: '越南',
    icon: 'menuIcon-service.png',
    sort: 6,
    visible: true,
    path: '/ranking/services',
    enabled: true,
    createdAt: '2020-10-10 10:10:10'
  },
  {
    id: 'm-7',
    parentId: null,
    type: 'menu',
    name: { zh: '用户中心', en: 'User Center', vi: 'Trung tâm người dùng' },
    region: '越南',
    icon: 'menuIcon-user.png',
    sort: 7,
    visible: true,
    path: '/ranking/user',
    enabled: true,
    createdAt: '2020-10-10 10:10:10'
  },
  {
    id: 'm-8',
    parentId: null,
    type: 'menu',
    name: { zh: '权益购买', en: 'Benefits', vi: 'Mua quyền lợi' },
    region: '越南',
    icon: 'menuIcon-benefit.png',
    sort: 8,
    visible: true,
    path: '/ranking/benefits',
    enabled: true,
    createdAt: '2020-10-10 10:10:10'
  }
];

/** 扁平化树，供上级菜单选择（排除指定节点及其子孙）。 */
export function flattenMenus(
  nodes: AdminMenu[],
  excludeId?: string,
  level = 0
): Array<AdminMenu & { level: number }> {
  const result: Array<AdminMenu & { level: number }> = [];
  const walk = (list: AdminMenu[], depth: number) => {
    list.forEach((node) => {
      if (excludeId && node.id === excludeId) return;
      result.push({ ...node, level: depth });
      if (node.children?.length) {
        walk(node.children, depth + 1);
      }
    });
  };
  walk(nodes, level);
  return result;
}

export function updateMenuInTree(
  nodes: AdminMenu[],
  id: string,
  updater: (node: AdminMenu) => AdminMenu
): AdminMenu[] {
  return nodes.map((node) => {
    if (node.id === id) {
      return updater(node);
    }
    if (node.children?.length) {
      return { ...node, children: updateMenuInTree(node.children, id, updater) };
    }
    return node;
  });
}

export function removeMenuFromTree(nodes: AdminMenu[], id: string): AdminMenu[] {
  return nodes
    .filter((node) => node.id !== id)
    .map((node) =>
      node.children?.length
        ? { ...node, children: removeMenuFromTree(node.children, id) }
        : node
    );
}

export function insertMenuIntoTree(
  nodes: AdminMenu[],
  parentId: string | null,
  menu: AdminMenu
): AdminMenu[] {
  if (!parentId) {
    return [...nodes, menu].sort((a, b) => a.sort - b.sort);
  }
  return nodes.map((node) => {
    if (node.id === parentId) {
      const children = [...(node.children ?? []), menu].sort((a, b) => a.sort - b.sort);
      return { ...node, children };
    }
    if (node.children?.length) {
      return { ...node, children: insertMenuIntoTree(node.children, parentId, menu) };
    }
    return node;
  });
}
